/**
 * `invoices` — factures et avoirs (base galerie, 4.1 ; numérotation 7.6).
 *
 * Une facture émise est immuable ; on la corrige par un avoir. Le numéro est
 * attribué à l'émission, jamais au brouillon.
 */
import { z } from 'zod';
import { categorieTvaSchema, regimeTvaSchema } from '../tax/regimes.js';
import { bilingueSchema, CHAMPS_DOCUMENT, centimesSchema, centimesPositifsSchema, deviseSchema, idSchema, tauxPointsDeBaseSchema, withAudit, } from './commun.js';
export const TYPES_FACTURE = ['facture', 'avoir'];
export const typeFactureSchema = z.enum(TYPES_FACTURE);
export const ETATS_FACTURE = ['brouillon', 'emise', 'payee', 'annulee'];
export const etatFactureSchema = z.enum(ETATS_FACTURE);
export const ligneFactureSchema = z.object({
    oeuvreId: idSchema.optional(),
    exemplaireId: idSchema.optional(),
    designation: z.string().min(1),
    quantite: z.int().min(1).default(1),
    prixUnitaireCentimes: centimesSchema,
    regimeTva: regimeTvaSchema.default('normal'),
    /** Prix d'acquisition, exigé par la TVA sur la marge. */
    prixAchatCentimes: centimesPositifsSchema.optional(),
    droitDeSuite: z.boolean().default(false),
});
/** Un total par catégorie de TVA, tel que l'attend la norme EN 16931 (7.6). */
export const totalCategorieTvaSchema = z.object({
    categorie: categorieTvaSchema,
    regime: regimeTvaSchema,
    tauxPointsDeBase: tauxPointsDeBaseSchema,
    baseCentimes: centimesSchema,
    tvaCentimes: centimesSchema,
    motifExoneration: z.string().optional(),
});
export const totauxFactureSchema = z.object({
    totalHtCentimes: centimesSchema,
    totalTvaCentimes: centimesSchema,
    totalTtcCentimes: centimesSchema,
    droitDeSuiteCentimes: centimesSchema.default(0),
    parCategorieTva: z.array(totalCategorieTvaSchema).default([]),
});
export const invoiceSchema = withAudit(z.object({
    _id: idSchema,
    /** Attribué à l'émission : « F-2026-0001 », « A-2026-0001 » (7.6). */
    numero: z.string().optional(),
    type: typeFactureSchema.default('facture'),
    contactId: idSchema,
    /** L'avoir désigne la facture qu'il corrige. */
    factureOrigineId: idSchema.optional(),
    lignes: z.array(ligneFactureSchema).min(1),
    devise: deviseSchema.default('EUR'),
    totaux: totauxFactureSchema.optional(),
    droitDeSuiteAChargeAcheteur: z.boolean().default(false),
    mentions: z.array(bilingueSchema).default([]),
    etat: etatFactureSchema.default('brouillon'),
    emiseLe: z.date().optional(),
    echeanceLe: z.date().optional(),
    reglements: z.array(idSchema).default([]),
    documentId: idSchema.optional(),
    /** Le XML Factur-X au profil EN 16931, embarqué dans le PDF/A-3 (D10). */
    facturXCle: z.string().optional(),
}));
export const invoiceCreationSchema = invoiceSchema.omit({
    ...CHAMPS_DOCUMENT,
    numero: true,
    totaux: true,
    etat: true,
    emiseLe: true,
    reglements: true,
    documentId: true,
    facturXCle: true,
});
/** Seul un brouillon se modifie : une facture émise est immuable (7.6). */
export const invoiceMiseAJourSchema = invoiceCreationSchema.partial();
//# sourceMappingURL=invoices.js.map